<script setup lang="ts">
import type { QRCode } from 'jsqr'
import { useI18n } from 'vue-i18n'

import BaseButton from '~/components/atoms/BaseButton.vue'
import BaseInput from '~/components/atoms/BaseInput.vue'
import TextButton from '~/components/atoms/TextButton.vue'
import BaseCluster from '~/components/foundations/BaseCluster.vue'
import BaseStack from '~/components/foundations/BaseStack.vue'
import BaseDialog from '~/components/molecules/BaseDialog.vue'
import QRCodeReader from '~/components/molecules/QRCodeReader.vue'

// MEMO: 現状DATEを指定しても特に実装はないです
type ScanType = 'TEXT' | 'NUMBER' | 'DATE'

const props = withDefaults(
  defineProps<{
    isActive: boolean
    type?: ScanType
  }>(),
  {
    type: 'TEXT',
  }
)

const emit = defineEmits<{
  'update:isActive': [value: boolean]
  submit: [value: string]
}>()

const { t } = useI18n()

const scannedValue = ref('')
const qrCodeReaderRef = ref<InstanceType<typeof QRCodeReader> | null>(null)
const regexp = new RegExp(`.+`)

const handleDetected = (code: QRCode) => {
  scannedValue.value = code.data
}

const validationError = computed(() => {
  if (!scannedValue.value) return ''

  if (props.type === 'NUMBER' && isNaN(Number(scannedValue.value))) {
    return t('数値のみ入力可能です。再度コードを読み取ってください。')
  }
  return ''
})

const canSubmit = computed(() => !!scannedValue.value && !validationError.value)

const submit = () => {
  if (scannedValue.value) {
    emit('submit', scannedValue.value)
    close()
  }
}

const clear = () => {
  scannedValue.value = ''
  qrCodeReaderRef.value?.resume()
}

const close = () => {
  scannedValue.value = ''
  emit('update:isActive', false)
}
</script>

<template>
  <BaseDialog
    :is-active="isActive"
    :title="$t('二次元コード読み取り')"
    :padding="{ top: '0px', bottom: '0px', left: '0px', right: '0px' }"
    overflow-y="hidden"
    @close="close"
    @cancel="close"
  >
    <template #content>
      <div class="dialog-content">
        <div class="reader-area">
          <QRCodeReader ref="qrCodeReaderRef" :regexp="regexp" @detected="handleDetected" />
        </div>
        <BaseStack gap="m" class="controls-area">
          <BaseInput
            :placeholder="$t('二次元コードを読み取ってください。')"
            :model-value="scannedValue"
            :class="{ 'is-validation-error': !!validationError }"
            readonly
          />
          <span v-if="validationError" class="error-message">
            {{ validationError }}
          </span>
          <BaseCluster gap="m" justify="flex-end">
            <TextButton :text="$t('クリア')" :disabled="!scannedValue" @click="clear" />
            <BaseButton :text="$t('登録')" color="primary" :disabled="!canSubmit" @click="submit" />
          </BaseCluster>
        </BaseStack>
      </div>
    </template>
  </BaseDialog>
</template>

<style scoped lang="scss">
.dialog-content {
  display: flex;
  flex-direction: column;

  @include mobile {
    height: calc(95svh - 48px);
  }
}

.reader-area {
  flex: 1;
  min-height: 0;
  // BaseDialogのmax-height(672px) - ヘッダー(48px) - controls-areaの最低高さ(180px)
  max-height: 444px;
  overflow: hidden;
  display: flex;
  align-items: center;

  :deep(> div) {
    height: 100%;
    width: 100%;
  }

  :deep(canvas) {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
}

.controls-area {
  flex-shrink: 0;
  padding: spacingToken.$spacing-m;
}

.is-validation-error {
  :deep(.base-input) {
    border-color: var(--semantic-tokens-danger-strong-border);
  }
}

.error-message {
  @include textToken.body-small;
  color: var(--semantic-tokens-danger-default-text);
}
</style>
